import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  Inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatDialogModule,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { NgToastService } from 'ng-angular-popup';
import { MatIconModule } from '@angular/material/icon';
import { RichTextEditorComponent } from '@/app/components/shared/rich-textEditor/rich-textEditor.component';
import { User } from '@/app/interfaces/user.interface';
import { UserService } from '@/app/services/user.service';
import {
  Subject,
  BehaviorSubject,
  takeUntil,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import { MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import { DomSanitizer } from '@angular/platform-browser';
import { ReloadPlanDataService } from '@/app/services/shared/reload-plan-data.service';
import { DialogUtilityService } from '@/app/services/utility/dialog-utility.service';
import { Router } from '@angular/router';

interface ApprovalState {
  isOpen: boolean;
  isApproved: boolean;
  isLoading: boolean;
  error: string | null;
  selectedUsers: User[];
  filteredUsers: User[];
  showDropdown: boolean;
  notes: string;
}

interface ApprovalDetail {
  feedback: string | null;
  status: string;
  userName: string;
  email:string;
  userTitle: string | null;
  user: any;
}

const INITIAL_STATE: ApprovalState = {
  isOpen: false,
  isApproved: false,
  isLoading: false,
  error: null,
  selectedUsers: [],
  filteredUsers: [],
  showDropdown: false,
  notes: '',
};

@Component({
  selector: 'app-send-for-approval',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    RichTextEditorComponent,
    MatIconModule,
    MatDialogModule,
    FormsModule,
    MatTableModule,
    MatSelectModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './send-for-approval.component.html',
  styleUrl: './send-for-approval.component.css',
})
export class SendForApprovalComponent implements OnInit, OnDestroy {
  @ViewChild('dropdownContainer') private dropdownContainer!: ElementRef;
  @ViewChild('inputFieldUser') private inputField!: ElementRef;
  pending: string = 'pending';
  Comment: boolean = false;
  private readonly destroy$ = new Subject<void>();
  user_info: any = localStorage.getItem('user_info')
    ? JSON.parse(localStorage.getItem('user_info') as string)
    : null;
  /**
   * A BehaviorSubject from RxJS, which holds the current state of the component.
   * BehaviorSubject always stores the latest value, and you can listen to its updates.
   * BehaviorSubject allows you to:
   *  Access the current value of the state via .value.
   *  Emit new values with .next().
   *
   * state$ is a BehaviorSubject that holds and manages the state (ApprovalState) for the component
   */

  private readonly state$ = new BehaviorSubject<ApprovalState>(INITIAL_STATE);
  private users: User[] = [];

  /**
   * .asObservable() converts the BehaviorSubject into a read-only observable (currentState$).
   * This ensures that external components or services can only subscribe to changes in the state but cannot modify it.
   */
  readonly currentState$ = this.state$.asObservable();

  readonly userControl = new FormControl<string>('');

  resetRichTextEditor = false;
  selectedUsers: User[] = []; // List of selected users shown in the table
  displayedColumns: string[] = ['name', 'title', 'status', 'action'];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public dialogueInjectedData: {
      projectId: number;
      stage: number;
      approvalResponses: ApprovalDetail[];
    },
    private dialogRef: MatDialogRef<SendForApprovalComponent>,
    private readonly toast: NgToastService,
    private readonly projectsService: ProjectsService,
    private readonly userService: UserService,
    private sanitizer: DomSanitizer,
    private reloadPlanDataService: ReloadPlanDataService,
    private dialogUtilityService: DialogUtilityService,
    private router: Router
  ) {
    this.setupInputSubscription();
  }

  ngOnInit(): void {
    this.initializeComponent();
    this.fetchUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getSanitizedHtml(note: string) {
    if (!note) {
      return '';
    }
    return this.sanitizer.bypassSecurityTrustHtml(note);
  }
  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    const currentState = this.state$.value;
    const newDropdownState = !currentState.showDropdown;

    this.updateState({
      showDropdown: newDropdownState,
    });

    if (newDropdownState) {
      this.filterUsers(this.userControl.value || '');
    }
  }

  onClickOutside(): void {
    this.updateState({ showDropdown: false });
  }

  onSelectionChange(user: User): void {
    const currentState = this.state$.value;
    if (
      !currentState.selectedUsers.some((selected) => selected.id === user.id)
    ) {
      this.updateState({
        selectedUsers: [...currentState.selectedUsers, user],
        showDropdown: false,
      });
      this.userControl.setValue('');
      this.filterUsers('');
    }
  }

  remove(user: User): void {
    const currentState = this.state$.value;
    this.updateState({
      selectedUsers: currentState.selectedUsers.filter(
        (selected) => selected.id !== user.id
      ),
    });
    this.filterUsers(this.userControl.value || '');
  }

  async openSendForApproval(): Promise<void> {
    this.updateState({ isOpen: true });

    this.fetchUsers();
  }

  addNote(content: string): void {
    this.updateState({ notes: content });
    if (content.trim()) {
      this.Comment = true; // Mark the field as "dirty" when user types
    }
  }

  async sendApprovalRequest(): Promise<void> {
    /**
     * check if all data is present and valid. if not show error toast
     */
    if (!this.dialogueInjectedData.projectId) {
      this.toast.danger('Project Id not found in child', 'ERROR');
      return;
    }

    const currentState = this.state$.value;
    if (currentState.selectedUsers.length === 0) {
      this.toast.danger('Please select at least one user', 'ERROR', 4000);
      return;
    }

    const subject =
      this.dialogueInjectedData.stage == 1
        ? 'Stage 1 Approval request'
        : 'Stage 2 Approval request';

    const transformedData = {
      // Approvers data
      project_id: this.dialogueInjectedData.projectId,
      stage: this.dialogueInjectedData.stage,
      subject: subject,
      notes: this.state$.value.notes,
      data: currentState.selectedUsers.map((user) => ({
        user_id: user.id,
        status: 'pending',
        designation: null,
      })),

      // TODO: these needs to be dynamic
      // Notification data
      event_type: `Project Event`,
      notification_type: `in_system`,
    };

    this.updateState({ isLoading: true });

    try {
      await this.projectsService
        .sendApproval(transformedData)
        .pipe(takeUntil(this.destroy$))
        .toPromise();

      this.handleSuccessfulSubmission();
    } catch (error) {
      console.log('The error is:', error);
      this.toast.danger('Comments Field is Required', 'ERROR', 4000);
    } finally {
      this.updateState({ isLoading: false });
    }
  }

  closeSendForApproval(): void {
    this.resetState();
  }

  private setupInputSubscription(): void {
    this.userControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.filterUsers(value || '');
      });
  }

  private async initializeComponent(): Promise<void> {
    const storedApprovalState = localStorage.getItem('isApproved') === 'true';
    this.updateState({ isApproved: storedApprovalState });
  }

  private filterUsers(value: string): void {
    const filterValue = value.toLowerCase().trim();
    const currentState = this.state$.value;

    const filtered = this.users.filter(
      (user) =>
        user.name.toLowerCase().includes(filterValue) &&
        !currentState.selectedUsers.some((selected) => selected.id === user.id)
    );

    this.updateState({ filteredUsers: filtered });
  }

  private async fetchUsers(): Promise<void> {
    this.updateState({ isLoading: true, error: null });

    try {
      const data = await this.userService
        .getUsers()
        .pipe(takeUntil(this.destroy$))
        .toPromise();

      if (data) {
        this.users = data;
        this.filterUsers('');
      }
    } catch (error) {
      console.log('The error is:', error);
      this.updateState({ error: 'Failed to load users' });
    } finally {
      this.updateState({ isLoading: false });
    }
  }

  private handleSuccessfulSubmission(): void {
    /**
     * TODO:
     * when user again opens the dialogue, we should fetch the approvals if there are any
     *  if there are approvals already send, show proper data and old approvals should be
     *  labelled or something else so they can be identified that these are already send
     *  so if user adds new user, they will be differentiated i.e new ones and old ones
     */

    this.reloadPlanDataService.reloadPlanData();
    this.resetState();
    this.updateState({ isApproved: true });

    // Send event back to parent
    this.dialogRef.close({ success: true });
  }

  private resetState(): void {
    this.updateState({
      ...INITIAL_STATE,
      isApproved: this.state$.value.isApproved,
    });
    this.resetRichTextEditor = true;
  }

  private updateState(partialState: Partial<ApprovalState>): void {
    this.state$.next({
      ...this.state$.value,
      ...partialState,
    });
  }
  resendApprovalRequest(userId: number) {
    this.projectsService.resendApproval(userId).subscribe({
      next: (response: any) => {
        if (response.meta.success) {
          this.handleSuccessfulSubmission();
        }
      },
      error: (error) => {
        console.error('Error resending approval request:', error);
        this.toast.danger('Failed to resend approval request', 'ERROR', 4000);
      },
    });
  }
  isUserApprover(approverId: number): boolean {
    return approverId !== this.user_info?.id;
  }
}
